import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, File, CheckCircle, Loader } from 'lucide-react';

interface FileUploadCardProps {
  onFileSelected: (file: File) => void;
  onFileRemoved: () => void;
  uploadedFile: File | null;
  isUploading: boolean;
  onSubmit: () => void;
  uploadStatus: 'idle' | 'success' | 'error';
}

const FileUploadCard: React.FC<FileUploadCardProps> = ({
  onFileSelected,
  onFileRemoved,
  uploadedFile,
  isUploading,
  onSubmit,
  uploadStatus,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      onFileSelected(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files;
    if (files && files.length > 0) {
      onFileSelected(files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileExtension = (filename: string): string => {
    return filename.split('.').pop()?.toUpperCase() || 'FILE';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl overflow-hidden shadow-lg border border-gray-200 bg-white"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-8 py-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
            <Upload className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Enviar Arquivo de Dados</h2>
            <p className="text-blue-100 text-sm mt-1">Faça upload do seu arquivo para análise</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-8">
        <AnimatePresence mode="wait">
          {!uploadedFile ? (
            // Upload Area
            <motion.div
              key="upload-area"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={handleClick}
                className={`relative border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all duration-300 ${
                  isDragging
                    ? 'border-blue-500 bg-blue-50 scale-105'
                    : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileInputChange}
                  className="hidden"
                  accept=".csv,.xlsx,.xls,.json"
                />

                {/* Animated Icon */}
                <motion.div
                  animate={isDragging ? { scale: 1.1, y: -10 } : { scale: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mb-4"
                >
                  <motion.div
                    animate={isDragging ? { rotate: 360 } : { rotate: 0 }}
                    transition={{ duration: 0.8, repeat: isDragging ? Infinity : 0 }}
                  >
                    <Upload className="w-16 h-16 text-blue-500 mx-auto" />
                  </motion.div>
                </motion.div>

                {/* Text */}
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {isDragging ? 'Solte o arquivo aqui!' : 'Arraste seu arquivo aqui'}
                </h3>
                <p className="text-gray-600 mb-4">
                  ou clique para selecionar um arquivo do seu computador
                </p>

                {/* Supported Formats */}
                <div className="inline-block bg-white rounded-lg px-4 py-2 border border-gray-200">
                  <p className="text-sm text-gray-600">
                    Formatos suportados: <strong>CSV, XLSX, XLS, JSON</strong>
                  </p>
                </div>
              </div>
            </motion.div>
          ) : (
            // File Preview
            <motion.div
              key="file-preview"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <div className="space-y-6">
                {/* File Preview Card */}
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      {/* File Icon */}
                      <motion.div
                        initial={{ scale: 0, rotate: -90 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                        className="w-16 h-16 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0 mt-1"
                      >
                        <File className="w-8 h-8 text-white" />
                      </motion.div>

                      {/* File Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-gray-900 text-lg break-all">
                          {uploadedFile.name}
                        </h4>
                        <div className="flex flex-wrap gap-4 mt-3">
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Tamanho</p>
                            <p className="font-semibold text-gray-900">
                              {formatFileSize(uploadedFile.size)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Tipo</p>
                            <p className="font-semibold text-gray-900">
                              {getFileExtension(uploadedFile.name)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Status</p>
                            <motion.div
                              animate={{ scale: [1, 1.1, 1] }}
                              transition={{ duration: 2, repeat: Infinity }}
                              className="flex items-center gap-1"
                            >
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <p className="font-semibold text-green-600">Pronto</p>
                            </motion.div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Remove Button */}
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={onFileRemoved}
                      disabled={isUploading}
                      className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 ml-4"
                      title="Remover arquivo"
                    >
                      <X className="w-5 h-5" />
                    </motion.button>
                  </div>
                </div>

                {/* Upload Button */}
                <motion.button
                  whileHover={!isUploading ? { scale: 1.02 } : {}}
                  whileTap={!isUploading ? { scale: 0.98 } : {}}
                  onClick={onSubmit}
                  disabled={isUploading}
                  className={`w-full py-4 px-6 rounded-lg font-bold text-white transition-all flex items-center justify-center gap-2 ${
                    isUploading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:shadow-lg'
                  }`}
                >
                  {isUploading ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      >
                        <Loader className="w-5 h-5" />
                      </motion.div>
                      <span>Enviando arquivo...</span>
                    </>
                  ) : uploadStatus === 'success' ? (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      <span>Arquivo enviado com sucesso!</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      <span>Enviar Arquivo para Análise</span>
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer Info */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-8 py-4 border-t border-gray-200">
        <p className="text-sm text-gray-600">
          💡 <strong>Dica:</strong> Seus dados são processados de forma segura e criptografada. O processamento típico leva de 2 a 5 minutos.
        </p>
      </div>
    </motion.div>
  );
};

export default FileUploadCard;
